/**
 * Audit Detail Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog showing full details of an audit log entry.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  User,
  Monitor,
  MapPin,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import type { AuditLogEntry, AuditLogDetailResponse } from '../../../types/admin';
import { SEVERITY_COLORS, SEVERITY_LABELS } from '../../../types/admin';
import { auditService } from '../../../services/admin';
import { Button } from '../../ui/button';

interface AuditDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: AuditLogEntry | null;
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({
  isOpen,
  onClose,
  entry,
}) => {
  const [detailData, setDetailData] = useState<AuditLogDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load full details when entry changes
  useEffect(() => {
    if (isOpen && entry) {
      setIsLoading(true);
      auditService
        .getLog(entry.id)
        .then(setDetailData)
        .catch((err) => {
          console.error('Failed to load log details:', err);
          // Fall back to using the entry we have
          setDetailData({ log: entry });
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, entry]);

  if (!isOpen || !entry) return null;

  const log = detailData?.log || entry;
  const changes = auditService.parseChanges(log.changes);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Audit Log Details
              </h2>
              <p className="text-sm text-gray-500">
                {auditService.formatEventType(log.event_type)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {isLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading details...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="flex items-center gap-4">
                  <span
                    className={`
                      px-3 py-1.5 rounded-full text-sm font-medium
                      ${SEVERITY_COLORS[log.severity]}
                    `}
                  >
                    {SEVERITY_LABELS[log.severity]}
                  </span>
                  <span className="text-lg font-medium text-gray-900">
                    {log.action}
                  </span>
                </div>

                {/* Metadata grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Timestamp */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock size={18} className="text-gray-400 mt-0.5" />
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">
                        Timestamp
                      </span>
                      <span className="text-sm text-gray-900">
                        {auditService.formatTimestamp(log.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* User */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <User size={18} className="text-gray-400 mt-0.5" />
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">
                        User
                      </span>
                      <span className="text-sm text-gray-900">
                        {log.user_email || 'System'}
                      </span>
                      {log.user_role && (
                        <span className="block text-xs text-gray-500 capitalize">
                          {log.user_role}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* IP Address */}
                  {log.ip_address && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <MapPin size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <span className="block text-xs text-gray-500 font-medium">
                          IP Address
                        </span>
                        <span className="text-sm text-gray-900 font-mono">
                          {log.ip_address}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* User Agent */}
                  {log.user_agent && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Monitor size={18} className="text-gray-400 mt-0.5" />
                      <div>
                        <span className="block text-xs text-gray-500 font-medium">
                          User Agent
                        </span>
                        <span className="text-sm text-gray-900 truncate max-w-[200px]" title={log.user_agent}>
                          {log.user_agent.length > 50
                            ? log.user_agent.slice(0, 50) + '...'
                            : log.user_agent}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Resource */}
                {log.resource_type && (
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={16} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Resource</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <span className="text-blue-600">Type:</span>
                        <span className="ml-2 text-blue-900 capitalize">
                          {log.resource_type}
                        </span>
                      </div>
                      {log.resource_id && (
                        <div>
                          <span className="text-blue-600">ID:</span>
                          <span className="ml-2 text-blue-900 font-mono text-xs">
                            {log.resource_id}
                          </span>
                        </div>
                      )}
                      {log.resource_name && (
                        <div className="col-span-2">
                          <span className="text-blue-600">Name:</span>
                          <span className="ml-2 text-blue-900">
                            {log.resource_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Client */}
                {log.client_id && (
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Client</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <span className="text-purple-600">ID:</span>
                        <span className="ml-2 text-purple-900 font-mono text-xs">
                          {log.client_id}
                        </span>
                      </div>
                      {log.client_name && (
                        <div>
                          <span className="text-purple-600">Name:</span>
                          <span className="ml-2 text-purple-900">
                            {log.client_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Changes */}
                {changes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Changes
                    </h3>
                    <div className="space-y-2">
                      {changes.map((change, i) => (
                        <div
                          key={i}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {change.key}
                          </span>
                          {change.oldValue !== undefined ? (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-sm text-red-600 line-through">
                                {formatValue(change.oldValue)}
                              </span>
                              <ArrowRight size={14} className="text-gray-400" />
                              <span className="text-sm text-green-600">
                                {formatValue(change.newValue)}
                              </span>
                            </div>
                          ) : (
                            <div className="mt-1">
                              <span className="text-sm text-gray-900">
                                {formatValue(change.newValue)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Checksum verification */}
                {detailData?.checksum && (
                  <div
                    className={`
                      p-3 rounded-lg flex items-center gap-2
                      ${detailData.checksum_valid
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                      }
                    `}
                  >
                    {detailData.checksum_valid ? (
                      <>
                        <CheckCircle size={16} className="text-green-600" />
                        <span className="text-sm text-green-700">
                          Log integrity verified
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} className="text-red-600" />
                        <span className="text-sm text-red-700">
                          Log integrity check failed
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Log ID */}
                <div className="text-xs text-gray-400 font-mono">
                  Log ID: {log.id}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper to format values for display
function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object]';
    }
  }
  return String(value);
}

export default AuditDetailModal;
